import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
    Table2,
    Bot,
    Sparkles,
    FilterX,
    Ruler,
    Settings,
    ChevronDown,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    ScrollText,
    ImageIcon,
    Tags,
} from 'lucide-react';

interface SidebarProps {
    currentPage: string;
    onNavigate: (page: string) => void;
    hasData: boolean;
    collapsed: boolean;
    onToggleCollapse: () => void;
}

interface NavItem {
    id: string;
    label: string;
    icon: typeof Table2;
}

const mainItems: NavItem[] = [
    { id: 'table', label: 'AI采集箱', icon: Table2 },
];

const configItems: NavItem[] = [
    { id: 'instances', label: '实例管理', icon: Bot },
    { id: 'title', label: '标题优化', icon: Sparkles },
    { id: 'image-edit', label: 'AI 图片编辑', icon: ImageIcon },
    { id: 'filter', label: '低质量图片过滤', icon: FilterX },
    { id: 'size', label: '尺码图识别', icon: Ruler },
    { id: 'sku', label: 'SKU 配置', icon: Tags },
    { id: 'log', label: '运行日志', icon: ScrollText },
];

const configPageIds = new Set(configItems.map(i => i.id));

export function Sidebar({ currentPage, onNavigate, hasData, collapsed, onToggleCollapse }: SidebarProps) {
    const configActive = configPageIds.has(currentPage);
    const [configOpen, setConfigOpen] = useState(configActive);

    return (
        <nav className={cn(
            'group/nav relative flex flex-col border-r border-[#e4e8eb] bg-[#f4f7f8] py-6 transition-all duration-300 ease-in-out',
            collapsed ? 'w-[58px]' : 'w-[270px]',
        )}>
            <div className="overflow-hidden px-8 pb-5">
                <span className={cn(
                    'text-lg font-bold text-[#20282c] transition-opacity duration-200',
                    collapsed ? 'opacity-0' : 'opacity-100',
                )}>AI板块</span>
            </div>

            <div className={cn('grid gap-1 px-5', collapsed ? 'grid-cols-1' : 'grid-cols-2')}>
                {mainItems.map(item => {
                    const Icon = item.icon;
                    const isActive = currentPage === item.id;
                    return (
                        <button
                            key={item.id}
                            onClick={() => onNavigate(item.id)}
                            title={collapsed ? item.label : undefined}
                            className={cn(
                                'flex items-center px-3 py-2.5 text-sm font-medium transition-all duration-200',
                                collapsed ? 'justify-center' : 'gap-2.5',
                                isActive
                                    ? 'bg-white text-[#159b96]'
                                    : 'text-[#56666d] hover:bg-white hover:text-[#159b96]',
                            )}
                        >
                            <Icon size={16} className="shrink-0" />
                            <span className={cn(
                                'truncate transition-all duration-300',
                                collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100',
                            )}>{item.label}</span>
                        </button>
                    );
                })}
            </div>

            <div className={cn('px-5', collapsed ? 'mt-2' : '-mt-[42px] ml-[127px]')}>
                <button
                    onClick={() => { if (!collapsed) setConfigOpen(v => !v); }}
                    title={collapsed ? '配置中心' : undefined}
                    className={cn(
                        'flex w-full items-center px-3 py-2.5 text-sm font-medium transition-all duration-200',
                        collapsed ? 'justify-center' : 'gap-2.5',
                        configActive
                            ? 'bg-white text-[#159b96]'
                            : 'text-[#20282c] hover:bg-white hover:text-[#159b96]',
                    )}
                >
                    <Settings size={16} className="shrink-0" />
                    <span className={cn(
                        'flex-1 text-left truncate transition-all duration-300',
                        collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100',
                    )}>配置中心</span>
                    {!collapsed && (configOpen ? <ChevronDown size={13} className="shrink-0" /> : <ChevronRight size={13} className="shrink-0" />)}
                </button>

                <div className={cn(
                    'mt-4 flex flex-col gap-1 overflow-hidden transition-all duration-300',
                    configOpen && !collapsed ? 'max-h-96 py-0.5 opacity-100' : 'max-h-0 opacity-0',
                    collapsed ? 'pl-0' : '-ml-[127px] w-[230px] pl-3',
                )}>
                    {configItems.map(item => {
                        const Icon = item.icon;
                        const isActive = currentPage === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => onNavigate(item.id)}
                                title={collapsed ? item.label : undefined}
                                className={cn(
                                    'flex items-center px-3 py-2 text-[12px] transition-all duration-200',
                                    collapsed ? 'justify-center' : 'gap-2.5',
                                    isActive
                                        ? 'bg-white font-medium text-[#129b96] shadow-sm'
                                        : 'text-[#708087] hover:bg-white hover:text-[#129b96]',
                                )}
                            >
                                <Icon size={14} className="shrink-0" />
                                <span className={cn(
                                    'truncate transition-all duration-300',
                                    collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100',
                                )}>{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* 底部折叠按钮 */}
            <div className="mt-auto px-2 pt-3">
                <button
                    onClick={onToggleCollapse}
                    className={cn(
                        'flex w-full items-center rounded-lg px-2 py-2 text-zinc-400 transition-all duration-200',
                        'hover:bg-zinc-200/80 hover:text-zinc-600',
                        'active:scale-95',
                        collapsed ? 'justify-center' : 'gap-2.5',
                    )}
                    title={collapsed ? '展开导航' : '收起导航'}
                >
                    {collapsed ? (
                        <ChevronsRight size={16} className="shrink-0" />
                    ) : (
                        <>
                            <ChevronsLeft size={16} className="shrink-0" />
                            <span className="text-xs font-medium">收起侧栏</span>
                        </>
                    )}
                </button>
            </div>
        </nav>
    );
}
